import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { Search, Ban, Check } from 'lucide-react';

const PAGE_SIZE = 20;

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ role: '', blocked: '', verified: '' });

  const [loadError, setLoadError] = useState('');
  const load = async () => {
    setLoading(true);
    setLoadError('');
    const f = {};
    if (filters.role) f.role = filters.role;
    if (filters.blocked === 'yes') f.blocked = true;
    if (filters.blocked === 'no') f.blocked = false;
    if (filters.verified === 'yes') f.verified = true;
    if (filters.verified === 'no') f.verified = false;
    const { data, total: t, error: err } = await adminService.listUsers(page, PAGE_SIZE, f);
    setUsers(data);
    setTotal(t ?? 0);
    if (err) setLoadError(err);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [page, filters.role, filters.blocked, filters.verified]);

  return (
    <div>
      <h1 className="font-heading font-bold text-2xl text-slate-900 mb-4">Users</h1>
      <div className="mb-4 flex flex-wrap gap-3">
        <select
          value={filters.role}
          onChange={(e) => { setFilters((f) => ({ ...f, role: e.target.value })); setPage(0); }}
          className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm"
        >
          <option value="">All roles</option>
          <option value="user">User</option>
          <option value="service_provider">Service provider</option>
        </select>
        <select
          value={filters.blocked}
          onChange={(e) => { setFilters((f) => ({ ...f, blocked: e.target.value })); setPage(0); }}
          className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm"
        >
          <option value="">All</option>
          <option value="yes">Blocked</option>
          <option value="no">Not blocked</option>
        </select>
        <select
          value={filters.verified}
          onChange={(e) => { setFilters((f) => ({ ...f, verified: e.target.value })); setPage(0); }}
          className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm"
        >
          <option value="">All</option>
          <option value="yes">Verified</option>
          <option value="no">Not verified</option>
        </select>
      </div>
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
        </div>
      ) : loadError ? (
        <div className="p-6 rounded-xl bg-amber-50 border border-amber-200 text-amber-800">
          <p className="font-medium">Could not load users</p>
          <p className="text-sm mt-1">{loadError}</p>
          <p className="text-sm mt-2">Ensure you ran the admin setup SQL (e.g. docs/run_admin_setup.sql) so admin can read user_profiles.</p>
        </div>
      ) : users.length === 0 ? (
        <div className="p-8 rounded-xl bg-white border border-slate-200 text-center text-slate-500">
          <p>No users found.</p>
          <p className="text-sm mt-1">Users appear here after they register. Check Supabase → Table Editor → user_profiles for data.</p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-700">Display name</th>
                  <th className="text-left p-3 font-medium text-slate-700">Email</th>
                  <th className="text-left p-3 font-medium text-slate-700">Role</th>
                  <th className="text-left p-3 font-medium text-slate-700">City</th>
                  <th className="text-left p-3 font-medium text-slate-700">Status</th>
                  <th className="text-left p-3 font-medium text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3">
                      <Link to={`/admin/users/${u.id}`} className="text-primary hover:underline font-medium">
                        {u.display_name || u.username || '—'}
                      </Link>
                      {u.is_dummy && <span className="ml-1 text-xs text-amber-600">(dummy)</span>}
                    </td>
                    <td className="p-3 text-slate-600">{u.email}</td>
                    <td className="p-3">{u.role}</td>
                    <td className="p-3">{u.city || '—'}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {u.is_blocked ? (
                          <span className="text-red-600 flex items-center gap-1">
                            <Ban className="h-4 w-4" /> Blocked
                          </span>
                        ) : (
                          <span className="text-slate-600">Active</span>
                        )}
                        {u.is_verified && (
                          <span className="text-emerald-600 flex items-center gap-1">
                            <Check className="h-4 w-4" /> Verified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <Link
                        to={`/admin/users/${u.id}`}
                        className="text-primary hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > PAGE_SIZE && (
            <div className="mt-4 flex justify-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 rounded-lg border border-slate-300 text-slate-700 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="py-1 text-slate-600">
                Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= total}
                className="px-3 py-1 rounded-lg border border-slate-300 text-slate-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminUsersPage;
